//apps\web\app\(tribute)\tribute\create

"use client";

import { useUser } from "@/lib/auth";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import TributeForm, { type TributeFormValues } from "../../../../components/tribute/TributeForm";

export default function TributeCreatePage() {
  const { isLoaded, isSignedIn } = useUser();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  // Optional: allow redirect_url override, but default to /tribute
  const redirectAfter = searchParams.get("redirectAfter") ?? "/tribute";

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      // Send them to sign-in, then back here
      router.replace("/sign-in?redirect_url=/tribute/create");
    }
  }, [isLoaded, isSignedIn, router]);

  if (!isLoaded || !isSignedIn) {
    return (
      <main className="container-ely py-10">
        <p className="text-sm text-muted-foreground">Preparing tribute form…</p>
      </main>
    );
  }

  async function handleCreate(values: TributeFormValues) {
    setError(null);

    try {
      const response = await fetch("/api/tributes", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.error ?? payload?.error?.message ?? "We weren’t able to save this tribute.";
        setError(message);
        return;
      }

      const tribute = await response.json();

      if (tribute?.id) {
        router.push(`/tribute/${tribute.id}`);
      } else {
        // Fallback if API doesn’t return the row for some reason
        router.push(redirectAfter);
      }
    } catch (_error) {
      setError("Something went wrong while saving. Please try again.");
    }
  }

  return (
    <main className="container-ely py-10">
      <div className="mb-6 flex items-center justify-between gap-3">
        <button type="button" onClick={() => router.back()} className="link text-xs text-muted-foreground">
          ← Back
        </button>
      </div>

      <div className="space-y-4">
        {error && (
          <div className="rounded-2xl border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}

        <TributeForm submitLabel="Create tribute" onSubmit={handleCreate} />
      </div>
    </main>
  );
}
